import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useDispatch, useSelector } from "react-redux";
import AdminSideBar from "../../common/AdminSideBar/AdminSideBar";
import { getBlogs, createBlog, updateBlog, deleteBlog } from "../../redux/blogSlice";
import {
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiArrowLeft, FiEye,
  FiFileText, FiAlertCircle, FiImage, FiUpload, FiX, FiLoader,
  FiUser, FiCalendar, FiTag, FiLink,
} from "react-icons/fi";
import { toast } from "react-toastify";

const CATEGORIES = [
  { value: "mentalHealth", label: "Mental Health" },
  { value: "therapy", label: "Therapy" },
  { value: "relationships", label: "Relationships" },
  { value: "personalGrowth", label: "Personal Growth" },
  { value: "parentingFamily", label: "Parenting & Family" },
  { value: "cultureIdentity", label: "Culture & Identity" },
  { value: "onlineTherapy", label: "Online Therapy" },
  { value: "selfCareWellbeing", label: "Self-Care & Wellbeing" },
  { value: "forTherapists", label: "For Therapists" },
];

const STATUS_OPTIONS = [
  { value: "published", label: "Published", color: "bg-green-100 text-green-700" },
  { value: "draft", label: "Draft", color: "bg-yellow-100 text-yellow-700" },
  { value: "archived", label: "Archived", color: "bg-gray-100 text-gray-500" },
];

const QUILL_MODULES = {
  toolbar: {
    container: [
      [{ header: [2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["blockquote", "link", "image"],
      ["clean"],
    ],
  },
};

const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "list", "bullet", "blockquote", "link", "image",
];

const slugify = (text) =>
  text.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

const emptyForm = () => ({
  title: "",
  content: "",
  author: "",
  publishDate: new Date().toISOString().slice(0, 10),
  category: "",
  tags: "",
  status: "draft",
  imageFile: null,
  imagePreview: "",
  imageAlt: "",
  ogImage: "",
  slug: "",
  metaTitle: "",
  metaDescription: "",
  focusKeyword: "",
  canonicalUrl: "",
});

const categoryLabel = (val) => CATEGORIES.find((c) => c.value === val)?.label || val;

const statusBadge = (status) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[1];
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${opt.color}`}>
      {opt.label}
    </span>
  );
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
};

export default function AdminBlog() {
  const dispatch = useDispatch();
  const { blogs, loading, saving } = useSelector((state) => state.blogs);

  const [view, setView] = useState("list");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formTab, setFormTab] = useState("basic");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [seoPreview, setSeoPreview] = useState(false);
  const quillRef = useRef(null);

  useEffect(() => {
    dispatch(getBlogs({ limit: 100, status: "all" }));
  }, [dispatch]);

  const filtered = useMemo(() => {
    return blogs.filter((b) => {
      const matchSearch = !searchQuery ||
        String(b.title).toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(b.author).toLowerCase().includes(searchQuery.toLowerCase());
      const matchStatus = filterStatus === "all" || b.status === filterStatus;
      const matchCat = filterCategory === "all" || b.category === filterCategory;
      return matchSearch && matchStatus && matchCat;
    });
  }, [blogs, searchQuery, filterStatus, filterCategory]);

  const openCreate = () => {
    setForm(emptyForm());
    setEditingId(null);
    setFormTab("basic");
    setView("form");
  };

  const openEdit = (blog) => {
    setForm({
      title: blog.title || "",
      content: blog.content || "",
      author: blog.author || "",
      publishDate: blog.publishDate ? blog.publishDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
      category: blog.category || "",
      tags: Array.isArray(blog.tags) ? blog.tags.join(", ") : (blog.tags || ""),
      status: blog.status || "draft",
      imageFile: null,
      imagePreview: blog.image || "",
      imageAlt: blog.imageAlt || "",
      ogImage: blog.ogImage || "",
      slug: blog.slug || "",
      metaTitle: blog.metaTitle || "",
      metaDescription: blog.metaDescription || "",
      focusKeyword: blog.focusKeyword || "",
      canonicalUrl: blog.canonicalUrl || "",
    });
    setEditingId(blog._id);
    setFormTab("basic");
    setView("form");
  };

  const handleTitleChange = (value) => {
    setForm((prev) => ({
      ...prev,
      title: value,
      slug: prev.slug && prev.slug !== slugify(prev.title) ? prev.slug : slugify(value),
      metaTitle: prev.metaTitle && prev.metaTitle !== prev.title ? prev.metaTitle : value,
    }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((prev) => ({ ...prev, imageFile: file, imagePreview: URL.createObjectURL(file) }));
  };

  const handleInsertImageUrl = () => {
    const url = window.prompt("Enter image URL:");
    if (!url) return;
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const range = quill.getSelection(true);
    quill.insertEmbed(range.index, "image", url);
    quill.setSelection(range.index + 1);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title is required."); setFormTab("basic"); return; }
    if (!form.author.trim()) { toast.error("Author is required."); setFormTab("basic"); return; }
    if (!form.content.trim() || form.content === "<p><br></p>") { toast.error("Content is required."); setFormTab("content"); return; }

    const fd = new FormData();
    fd.append("title", form.title.trim());
    fd.append("content", form.content);
    fd.append("author", form.author.trim());
    fd.append("status", form.status);
    if (form.publishDate) fd.append("publishDate", form.publishDate);
    if (form.category) fd.append("category", form.category);
    if (form.tags.trim()) {
      const tagsArr = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
      fd.append("tags", JSON.stringify(tagsArr));
    }
    if (form.imageFile) fd.append("image", form.imageFile);
    if (form.imageAlt.trim()) fd.append("imageAlt", form.imageAlt.trim());
    if (form.ogImage.trim()) fd.append("ogImage", form.ogImage.trim());
    if (form.slug.trim()) fd.append("slug", form.slug.trim());
    if (form.metaTitle.trim()) fd.append("metaTitle", form.metaTitle.trim());
    if (form.metaDescription.trim()) fd.append("metaDescription", form.metaDescription.trim());
    if (form.focusKeyword.trim()) fd.append("focusKeyword", form.focusKeyword.trim());
    if (form.canonicalUrl.trim()) fd.append("canonicalUrl", form.canonicalUrl.trim());

    try {
      if (editingId) {
        await dispatch(updateBlog({ id: editingId, formData: fd })).unwrap();
        toast.success("Blog post updated.");
      } else {
        await dispatch(createBlog(fd)).unwrap();
        toast.success("Blog post published.");
      }
      setView("list");
    } catch (err) {
      toast.error(err || "Something went wrong.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteBlog(id)).unwrap();
      toast.success("Blog post deleted.");
    } catch (err) {
      toast.error(err || "Failed to delete.");
    }
    setDeleteConfirm(null);
  };

  const seoScore = useMemo(() => {
    let score = 0;
    const checks = [];

    const titleLen = form.title?.length || 0;
    if (titleLen >= 30 && titleLen <= 60) { score += 15; checks.push({ ok: true, text: `Title: ${titleLen} chars (good)` }); }
    else { checks.push({ ok: false, text: `Title: ${titleLen} chars (aim 30–60)` }); }

    const metaLen = form.metaDescription?.length || 0;
    if (metaLen >= 120 && metaLen <= 160) { score += 20; checks.push({ ok: true, text: `Meta description: ${metaLen} chars (good)` }); }
    else { checks.push({ ok: false, text: `Meta description: ${metaLen} chars (aim 120–160)` }); }

    if (form.imageFile || form.imagePreview) { score += 10; checks.push({ ok: true, text: "Cover image set" }); }
    else { checks.push({ ok: false, text: "No cover image" }); }

    if (form.imageAlt?.trim()) { score += 5; checks.push({ ok: true, text: "Image alt text set" }); }
    else { checks.push({ ok: false, text: "Image alt text missing" }); }

    if (form.slug?.trim()) { score += 5; checks.push({ ok: true, text: "Slug set" }); }
    else { checks.push({ ok: false, text: "Slug not set" }); }

    if (form.focusKeyword?.trim()) {
      score += 10;
      checks.push({ ok: true, text: "Focus keyword set" });
      if (form.title?.toLowerCase().includes(form.focusKeyword.toLowerCase())) {
        score += 15; checks.push({ ok: true, text: "Keyword in title" });
      } else { checks.push({ ok: false, text: "Keyword not in title" }); }

      const stripped = form.content.replace(/<[^>]*>/g, "");
      if (stripped.toLowerCase().includes(form.focusKeyword.toLowerCase())) {
        score += 15; checks.push({ ok: true, text: "Keyword in content" });
      } else { checks.push({ ok: false, text: "Keyword not in content" }); }

      if (form.metaDescription?.toLowerCase().includes(form.focusKeyword.toLowerCase())) {
        score += 5; checks.push({ ok: true, text: "Keyword in meta description" });
      } else { checks.push({ ok: false, text: "Keyword not in meta description" }); }
    } else {
      checks.push({ ok: false, text: "Focus keyword not set" });
    }

    return { score, checks };
  }, [form]);

  const scoreColor = seoScore.score >= 80 ? "text-green-600" : seoScore.score >= 50 ? "text-yellow-500" : "text-red-500";
  const scoreBg = seoScore.score >= 80 ? "bg-green-100" : seoScore.score >= 50 ? "bg-yellow-100" : "bg-red-100";
  const scoreBar = seoScore.score >= 80 ? "bg-green-500" : seoScore.score >= 50 ? "bg-yellow-400" : "bg-red-400";

  const inputCls = "w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#625480]/30 focus:border-[#625480] bg-white";

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
      <AdminSideBar />

      <div className="flex-1 p-4 md:p-6 lg:p-8 min-w-0">

        {/* ══════════ LIST VIEW ══════════ */}
        {view === "list" && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Blog Posts</h1>
                <p className="text-gray-500 mt-1">{blogs.length} articles total</p>
              </div>
              <button onClick={openCreate} className="flex items-center gap-2 bg-[#625480] hover:bg-[#4e4268] text-white px-5 py-2.5 rounded-xl font-medium transition-colors shadow-sm">
                <FiPlus className="w-4 h-4" />
                New Blog Post
              </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="relative flex-1 min-w-48 max-w-sm">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="text" placeholder="Search title or author..." value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#625480]/30 focus:border-[#625480]" />
              </div>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2.5 border border-gray-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#625480]/30">
                <option value="all">All Statuses</option>
                {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2.5 border border-gray-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#625480]/30">
                <option value="all">All Categories</option>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
                  <FiLoader className="w-5 h-5 animate-spin" />
                  <span className="text-sm">Loading posts...</span>
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <FiFileText className="w-12 h-12 mb-3 opacity-40" />
                  <p className="text-sm">No blog posts found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Post</th>
                        <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Author</th>
                        <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Category</th>
                        <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Status</th>
                        <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden xl:table-cell">Published</th>
                        <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden xl:table-cell">SEO</th>
                        <th className="px-5 py-3.5"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filtered.map((blog) => {
                        const tLen = String(blog.title).length;
                        const mLen = String(blog.metaDescription || "").length;
                        const qs = Math.min(100,
                          (tLen >= 30 && tLen <= 60 ? 15 : 0) +
                          (mLen >= 120 && mLen <= 160 ? 20 : 0) +
                          (blog.image ? 10 : 0) +
                          (blog.imageAlt ? 5 : 0) +
                          (blog.slug ? 5 : 0) +
                          (blog.focusKeyword ? 45 : 0)
                        );
                        return (
                          <tr key={blog._id} className="hover:bg-gray-50/70 transition-colors">
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-3">
                                {blog.image ? (
                                  <img src={blog.image} alt="" className="w-12 h-9 object-cover rounded-lg flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                                ) : (
                                  <div className="w-12 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <FiImage className="w-4 h-4 text-gray-300" />
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <div className="font-medium text-gray-900 text-sm line-clamp-1">{blog.title}</div>
                                  {blog.slug && <div className="text-xs text-gray-400 mt-0.5 font-mono">/blog/{blog.slug}</div>}
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-4 hidden md:table-cell">
                              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                <FiUser className="w-3.5 h-3.5 text-gray-400" />
                                {blog.author || <span className="text-gray-300 italic">—</span>}
                              </div>
                            </td>
                            <td className="px-5 py-4 hidden lg:table-cell">
                              {blog.category ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#625480]/10 text-[#625480] text-xs font-medium">
                                  <FiTag className="w-3 h-3" />{categoryLabel(blog.category)}
                                </span>
                              ) : <span className="text-gray-300 text-xs italic">—</span>}
                            </td>
                            <td className="px-5 py-4 hidden lg:table-cell">{statusBadge(blog.status)}</td>
                            <td className="px-5 py-4 hidden xl:table-cell">
                              <div className="flex items-center gap-1.5 text-sm text-gray-500">
                                <FiCalendar className="w-3.5 h-3.5 text-gray-400" />
                                {formatDate(blog.publishDate || blog.createdAt)}
                              </div>
                            </td>
                            <td className="px-5 py-4 hidden xl:table-cell">
                              <div className="flex items-center gap-2">
                                <div className="w-14 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${qs >= 80 ? "bg-green-500" : qs >= 50 ? "bg-yellow-400" : "bg-red-400"}`} style={{ width: `${qs}%` }} />
                                </div>
                                <span className={`text-xs font-medium ${qs >= 80 ? "text-green-600" : qs >= 50 ? "text-yellow-500" : "text-red-500"}`}>{qs}%</span>
                              </div>
                            </td>
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-1 justify-end">
                                <button onClick={() => openEdit(blog)} className="p-2 hover:bg-blue-50 text-blue-500 rounded-lg transition-colors"><FiEdit2 className="w-4 h-4" /></button>
                                <button onClick={() => setDeleteConfirm(blog._id)} className="p-2 hover:bg-red-50 text-red-400 rounded-lg transition-colors"><FiTrash2 className="w-4 h-4" /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ══════════ FORM VIEW ══════════ */}
        {view === "form" && (
          <>
            <div className="flex items-center gap-4 mb-6">
              <button onClick={() => setView("list")} className="p-2 hover:bg-gray-200 rounded-lg transition-colors text-gray-600">
                <FiArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{editingId ? "Edit Blog Post" : "New Blog Post"}</h1>
                <p className="text-gray-500 text-sm mt-0.5">Fill in all fields and check your SEO score before publishing.</p>
              </div>
            </div>

            <div className="flex flex-col xl:flex-row gap-6">
              <div className="flex-1 min-w-0">
                {/* Tabs */}
                <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit">
                  {[
                    { id: "basic", label: "Basic Info" },
                    { id: "content", label: "Content" },
                    { id: "seo", label: "SEO" },
                    { id: "social", label: "Social" },
                  ].map((tab) => (
                    <button key={tab.id} onClick={() => setFormTab(tab.id)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${formTab === tab.id ? "bg-white text-[#625480] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* ── BASIC INFO ── */}
                {formTab === "basic" && (
                  <div className="space-y-5">
                    {/* Title */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Title <span className="text-red-400">*</span></label>
                      <input type="text" value={form.title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="Enter blog post title..." className={inputCls} />
                      <div className="flex justify-between mt-1.5">
                        <span className="text-xs text-gray-400">Auto-fills slug and SEO title</span>
                        <span className={`text-xs font-medium ${form.title.length >= 30 && form.title.length <= 60 ? "text-green-600" : "text-orange-500"}`}>{form.title.length}/60</span>
                      </div>
                    </div>

                    {/* Author + Date */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Author <span className="text-red-400">*</span></label>
                        <div className="relative">
                          <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <input type="text" value={form.author} onChange={(e) => setForm((p) => ({ ...p, author: e.target.value }))}
                            placeholder="Author full name..." className={`${inputCls} pl-9`} />
                        </div>
                      </div>
                      <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Publish Date</label>
                        <div className="relative">
                          <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <input type="date" value={form.publishDate} onChange={(e) => setForm((p) => ({ ...p, publishDate: e.target.value }))} className={`${inputCls} pl-9`} />
                        </div>
                      </div>
                    </div>

                    {/* Category + Tags */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                        <select value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} className={inputCls}>
                          <option value="">Select category...</option>
                          {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </div>
                      <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Tags</label>
                        <div className="relative">
                          <FiTag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <input type="text" value={form.tags} onChange={(e) => setForm((p) => ({ ...p, tags: e.target.value }))}
                            placeholder="anxiety, cbt, therapy..." className={`${inputCls} pl-9`} />
                        </div>
                        <p className="text-xs text-gray-400 mt-1.5">Comma-separated</p>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">Status</label>
                      <div className="flex gap-3">
                        {STATUS_OPTIONS.map((s) => (
                          <button key={s.value} onClick={() => setForm((p) => ({ ...p, status: s.value }))}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${form.status === s.value ? "border-[#625480] bg-[#625480] text-white" : "border-gray-200 text-gray-600 hover:border-[#625480]/40"}`}>
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Cover Image */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">Cover Image</label>
                      {form.imagePreview ? (
                        <div className="relative">
                          <img src={form.imagePreview} alt="Preview" className="w-full h-52 object-cover rounded-xl" onError={(e) => { e.target.style.display = "none"; }} />
                          <button onClick={() => setForm((p) => ({ ...p, imageFile: null, imagePreview: "" }))}
                            className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-white rounded-full shadow text-gray-600"><FiX className="w-4 h-4" /></button>
                          <label className="absolute bottom-2 right-2 flex items-center gap-1.5 bg-white/90 hover:bg-white px-3 py-1.5 rounded-lg text-xs font-medium text-gray-700 cursor-pointer shadow">
                            <FiUpload className="w-3.5 h-3.5" />Change
                            <input type="file" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" className="hidden" onChange={handleImageSelect} />
                          </label>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-[#625480]/50 hover:bg-gray-50 transition-all">
                          <FiImage className="w-8 h-8 text-gray-300 mb-2" />
                          <span className="text-sm text-gray-500 font-medium">Click to upload cover image</span>
                          <span className="text-xs text-gray-400 mt-1">JPEG, PNG, GIF, WEBP — max 10MB</span>
                          <input type="file" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" className="hidden" onChange={handleImageSelect} />
                        </label>
                      )}
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Image Alt Text</label>
                        <input type="text" value={form.imageAlt} onChange={(e) => setForm((p) => ({ ...p, imageAlt: e.target.value }))}
                          placeholder="Describe the image for accessibility and SEO..." className={inputCls} />
                      </div>
                    </div>
                  </div>
                )}

                {/* ── CONTENT ── */}
                {formTab === "content" && (
                  <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-sm font-semibold text-gray-700">Article Content <span className="text-red-400">*</span></label>
                      <button type="button" onClick={handleInsertImageUrl}
                        className="flex items-center gap-1.5 text-xs text-[#625480] hover:text-[#4e4268] border border-[#625480]/30 hover:border-[#625480] px-3 py-1.5 rounded-lg transition-colors">
                        <FiImage className="w-3.5 h-3.5" />Insert Image by URL
                      </button>
                    </div>
                    <div className="rounded-lg overflow-hidden border border-gray-200">
                      <ReactQuill ref={quillRef} theme="snow" value={form.content}
                        onChange={(val) => setForm((p) => ({ ...p, content: val }))}
                        modules={QUILL_MODULES} formats={QUILL_FORMATS}
                        placeholder="Write your blog content here..."
                        style={{ minHeight: 420 }} />
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Use H2/H3 headings and bullet lists for better SEO structure.</p>
                  </div>
                )}

                {/* ── SEO ── */}
                {formTab === "seo" && (
                  <div className="space-y-5">
                    {/* Focus Keyword */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Focus Keyword</label>
                      <input type="text" value={form.focusKeyword} onChange={(e) => setForm((p) => ({ ...p, focusKeyword: e.target.value }))}
                        placeholder="e.g. cognitive behavioral therapy" className={inputCls} />
                      <p className="text-xs text-gray-400 mt-1.5">The main keyword you want this post to rank for.</p>
                    </div>

                    {/* Slug */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">URL Slug</label>
                      <div className="flex items-stretch">
                        <span className="px-3 py-2.5 bg-gray-100 border border-r-0 border-gray-200 rounded-l-lg text-xs text-gray-500 font-mono flex items-center">/blog/</span>
                        <input type="text" value={form.slug} onChange={(e) => setForm((p) => ({ ...p, slug: slugify(e.target.value) }))}
                          className="flex-1 border border-gray-200 rounded-r-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#625480]/30 focus:border-[#625480]" />
                      </div>
                      <p className="text-xs text-gray-400 mt-1.5">Auto-generated from title. Lowercase letters, numbers, and hyphens only.</p>
                    </div>

                    {/* Meta Title */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">SEO Title</label>
                      <input type="text" value={form.metaTitle} onChange={(e) => setForm((p) => ({ ...p, metaTitle: e.target.value }))}
                        placeholder="Title shown in browser tab and search results..." className={inputCls} />
                      <div className="flex justify-between mt-1.5">
                        <span className="text-xs text-gray-400">Can differ from article title. Shown in Google results.</span>
                        <span className={`text-xs font-medium ${form.metaTitle.length >= 30 && form.metaTitle.length <= 60 ? "text-green-600" : "text-orange-500"}`}>{form.metaTitle.length}/60</span>
                      </div>
                    </div>

                    {/* Meta Description */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Meta Description</label>
                      <textarea value={form.metaDescription} onChange={(e) => setForm((p) => ({ ...p, metaDescription: e.target.value }))}
                        rows={3} placeholder="Short summary shown in Google search results..."
                        className={`${inputCls} resize-none`} />
                      <div className="flex justify-between mt-1.5">
                        <span className="text-xs text-gray-400">Aim for 120–160 characters for best click-through rates.</span>
                        <span className={`text-xs font-medium ${form.metaDescription.length >= 120 && form.metaDescription.length <= 160 ? "text-green-600" : "text-orange-500"}`}>{form.metaDescription.length}/160</span>
                      </div>
                    </div>

                    {/* Canonical URL */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Canonical URL</label>
                      <div className="relative">
                        <FiLink className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input type="url" value={form.canonicalUrl} onChange={(e) => setForm((p) => ({ ...p, canonicalUrl: e.target.value }))}
                          placeholder="https://mindtherapycounselling.com/blog/your-slug" className={`${inputCls} pl-9`} />
                      </div>
                      <p className="text-xs text-gray-400 mt-1.5">Leave empty to use the default URL. Only set if this content exists at another URL.</p>
                    </div>

                    {/* SERP Preview */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                      <button onClick={() => setSeoPreview(!seoPreview)}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-[#625480] transition-colors">
                        <FiEye className="w-4 h-4" />{seoPreview ? "Hide" : "Show"} Google Preview
                      </button>
                      {seoPreview && (
                        <div className="mt-4 rounded-xl p-4 bg-gray-50 border border-dashed border-gray-200">
                          <p className="text-xs text-gray-400 mb-3">How this post may appear in Google search results:</p>
                          <div className="max-w-lg bg-white rounded-lg p-4 border border-gray-100">
                            <p className="text-[#1a0dab] text-lg font-medium line-clamp-1">{form.metaTitle || form.title || "Your page title here"}</p>
                            <p className="text-[#006621] text-xs mt-1">mindtherapycounselling.com › blog › {form.slug || "your-slug"}</p>
                            <p className="text-gray-600 text-sm mt-1.5 line-clamp-2 leading-relaxed">
                              {form.metaDescription || form.content.replace(/<[^>]*>/g, "").slice(0, 160) || "Your meta description will appear here..."}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── SOCIAL ── */}
                {formTab === "social" && (
                  <div className="space-y-5">
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
                      Open Graph fields control how your post appears when shared on social media (Twitter, Facebook, LinkedIn, WhatsApp).
                    </div>

                    {/* OG Image */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">OG Image URL</label>
                      <div className="relative">
                        <FiImage className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input type="url" value={form.ogImage} onChange={(e) => setForm((p) => ({ ...p, ogImage: e.target.value }))}
                          placeholder="https://example.com/social-image.jpg" className={`${inputCls} pl-9`} />
                      </div>
                      <p className="text-xs text-gray-400 mt-1.5">
                        Recommended size: 1200×630px. Leave empty to use the cover image.
                      </p>
                      {form.ogImage && (
                        <div className="mt-3 rounded-xl overflow-hidden border border-gray-100">
                          <img src={form.ogImage} alt="OG preview" className="w-full h-40 object-cover"
                            onError={(e) => { e.target.style.display = "none"; }} />
                        </div>
                      )}
                    </div>

                    {/* Social Preview */}
                    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">Social Share Preview</h3>
                      <div className="border border-gray-200 rounded-xl overflow-hidden max-w-sm">
                        <div className="h-40 bg-gray-100 overflow-hidden">
                          <img
                            src={form.ogImage || form.imagePreview || ""}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                          {!form.ogImage && !form.imagePreview && (
                            <div className="w-full h-full flex items-center justify-center">
                              <FiImage className="w-8 h-8 text-gray-300" />
                            </div>
                          )}
                        </div>
                        <div className="p-3 bg-gray-50">
                          <p className="text-xs text-gray-400 uppercase tracking-wider">mindtherapycounselling.com</p>
                          <p className="text-sm font-semibold text-gray-900 mt-1 line-clamp-2">{form.metaTitle || form.title || "Post title"}</p>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{form.metaDescription || "Meta description will appear here."}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-100">
                  <button onClick={handleSave} disabled={saving}
                    className="flex items-center gap-2 bg-[#625480] hover:bg-[#4e4268] disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-2.5 rounded-xl font-medium transition-colors shadow-sm">
                    {saving && <FiLoader className="w-4 h-4 animate-spin" />}
                    {editingId ? "Save Changes" : "Publish Post"}
                  </button>
                  <button onClick={() => setView("list")} disabled={saving}
                    className="px-5 py-2.5 rounded-xl font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition-colors">
                    Cancel
                  </button>
                </div>
              </div>

              {/* ── SEO Score Panel ── */}
              <div className="xl:w-72 xl:flex-shrink-0">
                <div className="sticky top-8 space-y-4">
                  <div className={`rounded-xl border p-5 ${scoreBg}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-800">SEO Score</h3>
                      <span className={`text-2xl font-bold ${scoreColor}`}>{seoScore.score}%</span>
                    </div>
                    <div className="w-full h-2 bg-white/60 rounded-full overflow-hidden mb-4">
                      <div className={`h-full rounded-full transition-all duration-500 ${scoreBar}`} style={{ width: `${seoScore.score}%` }} />
                    </div>
                    <div className="space-y-2">
                      {seoScore.checks.map((check, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className={`mt-0.5 flex-shrink-0 w-3.5 h-3.5 rounded-full flex items-center justify-center text-white text-[10px] ${check.ok ? "bg-green-500" : "bg-gray-300"}`}>
                            {check.ok ? "✓" : "·"}
                          </span>
                          <span className={`text-xs leading-tight ${check.ok ? "text-gray-700" : "text-gray-500"}`}>{check.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3">SEO Quick Tips</h3>
                    <ul className="space-y-2 text-xs text-gray-600">
                      <li className="flex items-start gap-2"><FiAlertCircle className="w-3 h-3 mt-0.5 text-[#625480] flex-shrink-0" />Title: 30–60 chars including focus keyword</li>
                      <li className="flex items-start gap-2"><FiAlertCircle className="w-3 h-3 mt-0.5 text-[#625480] flex-shrink-0" />Meta description: 120–160 chars, include keyword</li>
                      <li className="flex items-start gap-2"><FiAlertCircle className="w-3 h-3 mt-0.5 text-[#625480] flex-shrink-0" />Cover image + alt text for image SEO</li>
                      <li className="flex items-start gap-2"><FiAlertCircle className="w-3 h-3 mt-0.5 text-[#625480] flex-shrink-0" />Use H2/H3 headings in content</li>
                      <li className="flex items-start gap-2"><FiAlertCircle className="w-3 h-3 mt-0.5 text-[#625480] flex-shrink-0" />OG image: 1200×630px for social sharing</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Delete Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <FiTrash2 className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Delete Post</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 text-sm">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} disabled={loading} className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white font-medium text-sm">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
